import { numberToUserDisplay } from '@ebiz/pretty-num';
import { camelize } from '@ember/string';
import { isNearInfinity } from '../utils/common';
import { getURL } from '../utils/resource';
import FilterConstants from '../filter-constants';
import { capitalizeFirstLetter } from '../utils/format';

import * as AditoolsSpice from '@ebiz/aditools-spice/netlists';
import type IndexModel from '../models/index';
import type StageDetailResults from '../models/results/sub-results/stage-details';
import type CircuitDetailResults from '../models/results/sub-results/circuit-details';
import type CdpService from '../services/cdp';
import type AssetsService from '../services/assets';
import type LoggerService from '../services/logger';
import FastBoot from 'ember-cli-fastboot/services/fastboot';

interface CmsData {
  Product: {
    id: string;
    creationDate: string;
    lastUpdateDate: string;
    launchDate: string;
    product: string;
    status: string;
    url: string;
    shortDesc: string;
    longDesc: string;
    category: string;
    categoryID: string;
    subCategory: string;
    subCategoryID: string;
    Videos: Record<string, string>[] | null;
    Webcasts: Record<string, string>[] | null;
    cftls: Record<string, string>[] | null;
    diagrams: Record<string, string>[] | null;
    evalBoards: Record<string, string>[] | null;
    pressReleases: Record<string, string>[] | null;
    AlternativeParts: Record<string, string>[] | null;
    CompanionProduct: Record<string, string>[] | null;
    ProductDocs: [ProductDocs];
  };
  menuItems: Record<string, string>[];
}

interface ProductDocs {
  Features: string;
  PstMap: Record<string, string>;
  Specifications: Record<string, string>[] | null;
  Documentation: Record<string, string>[] | null;
  DeviceDrivers: Record<string, string>[] | null;
  Hardware: Record<string, string>[] | null;
  SimulationModels: [SimulationModel];
}

interface SimulationModel {
  id: string;
  dataSheetRevisionNumber: string;
  fileSize: string;
  fileURL: string;
  languageCode: string;
  UID: string;
  nodename: string;
  optionalDesc: string;
  publishDate: string;
  lastUpdatedDate: string;
  releaseDate: string;
  title: string;
  titlePlain: string;
}

const netlistFileList: Record<string, string> = {
  AC: 'ACAnalysis',
  NOISE: 'NoiseAnalysis',
  TRAN: 'TransientAnalysis',
};

export async function getSpiceNetListFiles(
  pathURL: string,
  mediaURL: string,
  locale: string,
  opamps: string[],
  downloadType: string,
  modelFiles: Record<string, string>,
  model: IndexModel,
  assetService: AssetsService,
  loggerService: LoggerService,
  fastbootService: FastBoot,
): Promise<{ downloadFiles: Array<DownloadFile>; netListParts: Array<{ partName: string; type: string }> }> {
  const downloadFiles: Array<DownloadFile> = [];
  const listOfParts = [];
  if (checkModelFiles(modelFiles)) {
    const modelFile = getModelFile(getMacroModelInfo([...new Set(opamps)], modelFiles));
    [...new Set(opamps)].map((item) => {
      listOfParts.push({ partName: item, type: 'Netlist' });
    });
    for (const cir in netlistFileList) {
      if (Object.prototype.hasOwnProperty.call(netlistFileList, cir)) {
        const netlist = new AditoolsSpice.SpiceNetList(
          await getTemplateFile(
            getURL(pathURL + netlistFileList[cir] + '.txt', fastbootService, loggerService),
            assetService,
          ),
        );

        // Replace Title
        netlist.updateTitle(buildNetListTitle(model, opamps, cir));

        // Replace Input Signal
        let minInput = 0,
          maxInput = 0,
          inputFrequency = 0;
        const voltageRangeObject = model.results.components.voltageRange.detailsOfVoltageRange;
        if (voltageRangeObject !== null && voltageRangeObject.length > 0) {
          minInput = voltageRangeObject[0].nominalVoltageDisplay.maxSignals.inputRange.minimum;
          maxInput = voltageRangeObject[0].nominalVoltageDisplay.maxSignals.inputRange.maximum;
          inputFrequency = voltageRangeObject[0].nominalVoltageDisplay.maxSignals.inputRange.frequency;
        }
        const inputSignalExtraParams = getCirInputSignal(netlist, cir, minInput, maxInput, inputFrequency);

        // Replace Voltages
        netlist.updateElementValue(
          'VP',
          numberToUserDisplay(model.inputs.components.vsPlus, 'Spice', 3, { showZeroes: false }),
        );
        netlist.updateElementValue(
          'VM',
          numberToUserDisplay(model.inputs.components.vsMinus, 'Spice', 3, { showZeroes: false }),
        );

        // Replace Simulation Directives
        netlist.updateACAnalysis({
          sweepType: 'decade',
          points: 100,
          startFrequency: model.results.specifications.filterFrequencyRange.startFrequency,
          stopFrequency: model.results.specifications.filterFrequencyRange.stopFrequency,
        });
        netlist.updateNoiseAnalysis({
          sweepType: 'decade',
          points: 100,
          startFrequency: model.results.specifications.filterFrequencyRange.startFrequency,
          stopFrequency: model.results.specifications.filterFrequencyRange.stopFrequency,
        });
        netlist.updateTransientAnalysis({
          step: 0.000000001,
          stopTime: Math.max(...model.results.specifications.timeAxis),
        });

        // Build Stages Here
        const detailsOfStages: StageDetailResults[] = model.results.components.detailsOfStages;
        const detailsOfCircuits: CircuitDetailResults[] = model.results.tolerances.detailsOfCircuits;
        let stagesTemplate = '';
        for (let i = 0; i < detailsOfStages.length; i++) {
          const stageDetail = detailsOfStages[i];
          const circuitDetail = detailsOfCircuits[i];
          const currentStageLetter = stageDetail ? stageDetail.stageLetter : '';
          const stageImplementation = stageDetail ? stageDetail.implementation.replace(/ /g, '') : '';
          const stageType = stageDetail ? camelize(stageDetail.filterName.substring(10).replace(/_/g, '-')) : '';
          const stageGain = circuitDetail ? (isGainCircuit(circuitDetail) ? 'GainStage' : 'Stage') : '';
          const templateStageName = stageImplementation + capitalizeFirstLetter(stageType) + stageGain;
          const stageName = stageImplementation + stageType + stageGain;
          let templateFile = await getTemplateFile(pathURL + templateStageName + '.txt', assetService);
          if (model.results.components.needReferenceStage) {
            templateFile = replaceAll(templateFile, 'GND', 'REF');
          }
          const stageNetlist = new AditoolsSpice.SpiceNetList(templateFile);
          stageNetlist.updateSubcircuitName(stageName, stageName + currentStageLetter);
          const schematicValues = circuitDetail ? getSchematicValues(circuitDetail, modelFiles) : {};
          // Update the values for the component elements and stages
          for (const item in schematicValues) {
            if (Object.prototype.hasOwnProperty.call(schematicValues, item)) {
              let itemValue = schematicValues[item];
              if (itemValue) {
                if (itemValue === FilterConstants.Limits.NEARLY_INFINITY) {
                  itemValue = FilterConstants.Limits.INFINITY;
                }
                stageNetlist.updateElementValue(
                  item,
                  typeof itemValue === 'number'
                    ? numberToUserDisplay(itemValue, 'Spice', 3, { showZeroes: false })
                    : itemValue,
                );
              }
            }
          }
          stagesTemplate += stageNetlist.netlist + '\n\n';
        }
        // Update the CirStages Section
        let netlistTemplate = netlist.netlist.replace(
          '{CirStages}',
          getCirStages(detailsOfStages, detailsOfCircuits, model.results.components.needReferenceStage),
        );
        // Update Reference Voltage, if any
        const voltageSupplies = model.results.components.needReferenceStage
          ? `VR VREF 0 ${numberToUserDisplay(model.inputs.components.vsPlus / 2, 'Spice', 3, { showZeroes: false })}\n`
          : '';
        netlistTemplate = netlistTemplate.replace('{RefVoltage}', voltageSupplies);
        // Update extra input signal parameters
        netlistTemplate = netlistTemplate
          .replace('{dcVoltage}', inputSignalExtraParams.dcVoltage)
          .replace('{vNoise}', inputSignalExtraParams.vNoise);
        // Comment out unused input signal and simulation directives
        switch (cir) {
          case 'AC':
            netlistTemplate = netlistTemplate.replace('.TRAN', '*.TRAN').replace('.NOISE', '*.NOISE');
            break;
          case 'NOISE':
            netlistTemplate = netlistTemplate.replace('.TRAN', '*.TRAN').replace('.AC', '*.AC').replace('VIN', '* VIN');
            break;
          case 'TRAN':
            netlistTemplate = netlistTemplate.replace('.AC', '*.AC').replace('.NOISE', '*.NOISE');
            break;
          default:
            break;
        }
        downloadFiles.push({
          name: netlistFileList[cir] + '.cir',
          value: netlistTemplate + stagesTemplate + modelFile,
          location: downloadType === 'all' ? 'SPICE Files\\Netlists' : 'Netlists',
          type: 'LTSPICE',
        });
      }
    }
  } else {
    for (const name in modelFiles) {
      if (modelFiles[name] !== '') listOfParts.push({ partName: name, type: 'Netlist' });
    }
  }
  if (downloadFiles.length > 0) {
    const readmeLocale = locale === 'ru' ? 'en' : locale;
    const netlistReadme = await assetService.getAsset(
      getURL(`${mediaURL}/${readmeLocale}/designtools/filterwizard/spice/README.txt`, fastbootService, loggerService),
      'TEXT',
    );
    if (netlistReadme !== null) {
      downloadFiles.push({
        name: 'README.txt',
        value: netlistReadme,
        location: downloadType === 'all' ? 'SPICE Files\\Netlists' : 'Netlists',
        type: 'LTSPICE',
      });
    }
  }
  return { downloadFiles: downloadFiles, netListParts: listOfParts };
}

export function getModelFiles(opamps: string[], cdp: CdpService): PromiseHash {
  const fileHash: PromiseHash = {};
  for (let i = 0; i < opamps.length; i++) {
    const opamp = opamps[i];
    if (opamp) fileHash[opamp] = getModelFileForGeneric(opamp, cdp);
  }
  return fileHash;
}

function getSchematicValues(
  circuit: CircuitDetailResults,
  modelFiles: Record<string, string>,
): Record<string, string | number> {
  const schematicValues: Record<string, string | number> = {};
  if (circuit.c1 && circuit.c1.value !== null) {
    schematicValues['C1'] = circuit.c1.value ?? '';
  }
  if (circuit.c2 && circuit.c2.value !== null) {
    schematicValues['C2'] = circuit.c2.value ?? '';
  }
  if (circuit.c5 && circuit.c5.value !== null) {
    schematicValues['C5'] = circuit.c5.value ?? '';
  }
  if (circuit.r1 && circuit.r1.value !== null) {
    schematicValues['R1'] = circuit.r1.value ?? '';
  }
  if (circuit.r2 && circuit.r2.value !== null && !isNearInfinity(circuit.r2.value)) {
    schematicValues['R2'] = circuit.r2.value ?? '';
  }
  if (circuit.r3 && circuit.r3.value !== null && circuit.r4 && circuit.r4.value !== null && isGainCircuit(circuit)) {
    schematicValues['R3'] = circuit.r3.value ?? '';
    schematicValues['R4'] = circuit.r4.value ?? '';
  }
  if (circuit.r5 && circuit.r5.value !== null) {
    schematicValues['R5'] = circuit.r5.value ?? '';
  }
  if (circuit.l1 && circuit.l1.value !== null) {
    schematicValues['L1'] = circuit.l1.value ?? '';
  }
  if (circuit.opAmp && circuit.opAmp.name) {
    const opampName = circuit.opAmp.name;
    if (opampName) {
      const modelFile = modelFiles[opampName];
      if (modelFile) {
        const netlist = new AditoolsSpice.SpiceNetList(modelFile);
        const x1 = buildXString(netlist, 0);
        schematicValues['X1'] = x1.xString;
      }
    }
  }
  return schematicValues;
}

function isGainCircuit(circuit: CircuitDetailResults): boolean {
  if (circuit.r3.value !== null && circuit.r4.value !== null) {
    if (circuit.implementation === FilterConstants.implementationType.multipleFeedback) {
      return false;
    } else if (circuit.implementation === FilterConstants.implementationType.deliyannisFriend) {
      return !(circuit.r3.value === 0 && isNearInfinity(circuit.r4.value));
    } else {
      return !(isNearInfinity(circuit.r3.value) && circuit.r4.value === 0);
    }
  }
  return false;
}

function getModelFileForGeneric(partNumber: string, cdp: CdpService): Promise<string> {
  return cdp.getCmsData(partNumber.toLowerCase()).then((data: CmsData | null) => {
    if (!data) return '';
    const json: SimulationModel = data['Product']['ProductDocs'][0]['SimulationModels'][0];
    let fileName = '';
    if (typeof json !== 'undefined' && json) {
      fileName = json['fileURL'];
      fileName = fileName.substring(fileName.indexOf('=') + 1);
      fileName = _proxyIfLocal(fileName);
      const url = `${fileName}?modeltype=spice-models`;
      return fetch(url)
        .then((response) => {
          if (response.status !== 200) {
            throw `HTTP Request Failed fetching ${url}: ${response.status} ${response.statusText}`;
          }
          return response.text();
        })
        .then((text) => {
          if (text.indexOf(`Page Not Found`) !== -1) {
            console.log('Unexpected: 200 response status with embedded "Page Not Found" for CIR file - url: ', url);
            throw `HTTP Request Succeeded but contained Page Not Found - url: ${url}`;
          }
          return text;
        })
        .catch(() => {
          return '';
        });
    } else {
      console.log(`SPICE model doesn't exist for ${partNumber}`);
      return '';
    }
  });
}

function getMacroModelInfo(
  opamps: string[],
  modelFiles: Record<string, string>,
): Record<string, Record<string, string>> {
  const result: Record<string, Record<string, string>> = {};
  opamps.forEach((opamp) => {
    const modelFile = modelFiles[opamp];
    if (modelFile) {
      const netlist = new AditoolsSpice.SpiceNetList(modelFile);
      const subcktName = modelFile !== '' ? netlist.getSubcircuitName() : opamp;
      result[opamp] = { subcktName: subcktName, data: modelFile };
    }
  });
  return result;
}

function getModelFile(macroModelInfo: Record<string, Record<string, string>>): string {
  let result = '';
  const modelFiles: Record<string, string> = {};
  for (const opamp in macroModelInfo) {
    if (Object.prototype.hasOwnProperty.call(macroModelInfo, opamp)) {
      const infoItem = macroModelInfo[opamp];
      if (infoItem && infoItem['subcktName'] && infoItem['data']) {
        modelFiles[infoItem['subcktName']] = infoItem['data'];
      }
    }
  }
  for (const name in modelFiles) {
    if (Object.prototype.hasOwnProperty.call(modelFiles, name)) {
      let modelFile = modelFiles[name];
      let crlf = '';
      if (modelFile?.endsWith('\n')) {
        if (!modelFile.endsWith('\r\n')) {
          // Remove LF so that CR+LF can be appended to it
          modelFile = modelFile.slice(0, -1);
          crlf = '\r\n';
        }
      } else {
        crlf = '\r\n';
      }
      result += modelFile + crlf;
    }
  }
  return result;
}

function _proxyIfLocal(url: string): string {
  return window.location.hostname === 'localhost' ? url.substring(url.search(/[^/][/][^/]/) + 1) : url;
}

function checkModelFiles(modelFiles: Record<string, string>): boolean {
  const isValid = true;
  for (const name in modelFiles) {
    if (modelFiles[name] === '') return false;
  }
  return isValid;
}

function getTemplateFile(templateFileName: string, assetService: AssetsService): Promise<string> {
  return assetService.getAsset(templateFileName, 'TEXT').then((content: string) => {
    return content;
  });
}

function getCirInputSignal(
  netlist: AditoolsSpice.SpiceNetList,
  cir: string,
  MaxInputSignalLow: number,
  MaxInputSignalHigh: number,
  MaxInputSignalFrequency: number,
): { dcVoltage: string; vNoise: string } {
  // calculate average
  const iSignalMid = (MaxInputSignalLow + MaxInputSignalHigh) / 2;
  const iSignalMidFormatted = numberToUserDisplay(iSignalMid, 'Spice', 3, { showZeroes: false });

  // calculate amplitude
  const iSignalAmplitude = (MaxInputSignalHigh - MaxInputSignalLow) / 2;

  let vNoise = '';
  let dcVoltage = '';

  switch (cir) {
    case 'AC':
      netlist.updateSineVoltageSource('VIN', {
        offupdate: iSignalMid,
        peakAmplitude: iSignalAmplitude,
        frequency: MaxInputSignalFrequency,
      });
      dcVoltage = iSignalMidFormatted;
      vNoise = '* VNOISE IN 0 AC 0 DC ' + iSignalMidFormatted;
      break;
    case 'TRAN':
      netlist.updatePulseVoltageSource('VIN', {
        lowValue: iSignalMid,
        highValue: MaxInputSignalHigh,
        delayTime: 0,
        riseTime: 0.000000001,
        fallTime: 0.000000001,
        pulseWidth: 1,
        period: 2,
      });
      dcVoltage = iSignalMidFormatted;
      vNoise = '* VNOISE IN 0 AC 0 DC ' + iSignalMidFormatted;
      break;
    case 'NOISE':
      netlist.updateSineVoltageSource('VIN', {
        offupdate: iSignalMid,
        peakAmplitude: iSignalAmplitude,
        frequency: MaxInputSignalFrequency,
      });
      vNoise = 'VNOISE IN 0 AC 0 DC ' + iSignalMidFormatted;
      break;
  }
  return { dcVoltage: dcVoltage, vNoise: vNoise };
}

function getCirStages(
  detailsOfStages: StageDetailResults[],
  detailsOfCircuits: CircuitDetailResults[],
  refExistsFlag: boolean,
): string {
  let cirStages = '';
  const refValue = refExistsFlag === true ? 'VREF' : '0';
  const stageCount = detailsOfStages.length;
  let prevStageLetter = '';

  for (let i = 0; i < stageCount; i++) {
    const stageDetail = detailsOfStages[i];
    const circuitDetail = detailsOfCircuits[i];
    const currentStageLetter = stageDetail ? stageDetail.stageLetter : '';
    const stageImplementation = stageDetail ? stageDetail.implementation.replace(/ /g, '') : '';
    const stageType = stageDetail ? camelize(stageDetail.filterName.substring(10).replace(/_/g, '-')) : '';
    const stageGain = circuitDetail ? (isGainCircuit(circuitDetail) ? 'GainStage' : 'Stage') : '';
    const stageImplementationText = stageImplementation + stageType + stageGain;
    let oneCirStage = '';
    if (stageCount === 1) {
      oneCirStage = `X${currentStageLetter} IN OUT VCCG VEEG ${refValue} ${stageImplementationText}${currentStageLetter}`;
    } else {
      if (i === 0) {
        // XA IN OUTA VCCG VEEG 0 {StageTypeAVar}{StageLetterAVar}
        oneCirStage = `X${currentStageLetter} IN OUT${currentStageLetter} VCCG VEEG ${refValue} ${stageImplementationText}${currentStageLetter}`;
      } else if (i === stageCount - 1) {
        // XC OUTB OUT VCCG VEEG 0 {StageTypeCVar}{StageLetterCVar}
        oneCirStage = `\nX${currentStageLetter} OUT${prevStageLetter} OUT VCCG VEEG ${refValue} ${stageImplementationText}${currentStageLetter}`;
      } else {
        // XB OUTA OUTB VCCG VEEG 0 {StageTypeBVar}{StageLetterBVar}
        oneCirStage = `\nX${currentStageLetter} OUT${prevStageLetter} OUT${currentStageLetter} VCCG VEEG ${refValue} ${stageImplementationText}${currentStageLetter}`;
      }
    }
    prevStageLetter = currentStageLetter;
    cirStages += oneCirStage;
  }
  return cirStages;
}

function buildXString(
  netlist: AditoolsSpice.SpiceNetList,
  lastFloatIndex: number,
): { xString: string; lastFloatIndex: number } {
  const DEFAULT_PIN_COUNT = 5;
  const extraPins = netlist.getSubcircuitPortCount('.subckt') - DEFAULT_PIN_COUNT;
  const x1Items = [];
  let floatIndex = lastFloatIndex;
  for (let i = 0; i < extraPins; i++) {
    x1Items.push('PDFloat' + (++floatIndex).toString());
  }
  x1Items.push(netlist.getSubcircuitName());
  return { xString: x1Items.join(' '), lastFloatIndex: floatIndex };
}

function replaceAll(str: string, find: string, replace: string): string {
  return str.replace(new RegExp(find, 'g'), replace);
}

function buildNetListTitle(model: IndexModel, opAmpList: string[], choice: string): string {
  // Set Filter Response + Number of Stages
  const responseText =
    getOrdinal(model.results.specifications.nthOrder) + ' order ' + model.results.specifications.responseType;
  const responseValue = model.results.specifications.filterResponseValue
    ? ' ' +
      model.results.specifications.filterResponseValue.toString() +
      (model.results.specifications.responseType === 'Chebyshev' ? ' dB' : '')
    : '';
  const stageText =
    model.results.specifications.numberOfStages +
    (model.results.specifications.numberOfStages > 1 ? ' stages' : ' stage');

  // Set Passband Frequency Text
  const passFreq = numberToUserDisplay(model.inputs.specifications.passbandFrequency, 'Spice', 3, {
    showZeroes: false,
  });

  const netlistType = choice === 'AC' ? 'AC' : choice === 'NOISE' ? 'Noise' : 'Transient';

  return `* ${netlistType} Analysis, ${passFreq} ${
    model.inputs.filterType
  }, ${responseText}${responseValue}, ${stageText} using ${[...new Set(opAmpList)].join(', ')}`;
}

function getOrdinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'],
    v = n % 100;
  return n.toString() + (s[(v - 20) % 10] || s[v] || s[0]);
}
